import type { Username } from '../utils/constants';
import type { CallMode } from './callApi';

export type CallSocketMessage =
  | { type: 'socket-ready'; username: Username }
  | { type: 'pong' }
  | { type: 'call-started'; callId: string; receiver: Username; mode: CallMode; verificationCode?: string; warning?: string }
  | { type: 'incoming-call'; callId: string; caller: Username; mode: CallMode; verificationCode?: string }
  | { type: 'call-accepted'; callId: string }
  | { type: 'call-rejected'; callId: string }
  | { type: 'call-ended'; callId: string }
  | { type: 'offer'; callId: string; offer: RTCSessionDescriptionInit }
  | { type: 'answer'; callId: string; answer: RTCSessionDescriptionInit }
  | { type: 'ice'; callId: string; candidate: RTCIceCandidateInit }
  | { type: 'call-error'; message: string };

export type CallSocket = {
  socket: WebSocket;
  ready: Promise<void>;
  send: (message: unknown) => void;
  close: () => void;
};

type CallSocketHandlers = {
  onMessage: (message: CallSocketMessage) => void;
  onError: (message: string) => void;
  onOpen?: () => void;
  onClose?: () => void;
};

export function createCallSocket(username: Username, { onMessage, onError, onOpen, onClose }: CallSocketHandlers): CallSocket {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const url = new URL(`${protocol}//${window.location.host}/api/call/socket`);
  url.searchParams.set('username', username);
  console.info('CALL_WS_CONNECTING', username);
  const socket = new WebSocket(url);
  let pingTimer: number | null = null;
  const sendVisibility = () => {
    if (socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type: 'visibility', visible: document.visibilityState === 'visible' }));
    }
  };
  const ready = new Promise<void>((resolve, reject) => {
    socket.addEventListener(
      'open',
      () => {
        console.info('CALL_WS_OPEN', username);
        pingTimer = window.setInterval(() => {
          if (socket.readyState === WebSocket.OPEN) {
            console.info('CALL_WS_SEND', 'ping');
            socket.send(JSON.stringify({ type: 'ping' }));
          }
        }, 25000);
        sendVisibility();
        document.addEventListener('visibilitychange', sendVisibility);
        onOpen?.();
        resolve();
      },
      { once: true },
    );
    socket.addEventListener(
      'error',
      () => {
        console.info('CALL_WS_ERROR', username);
        reject(new Error('Call signaling connection failed.'));
      },
      { once: true },
    );
  });

  socket.addEventListener('message', (event) => {
    if (typeof event.data !== 'string') {
      return;
    }

    try {
      const message = JSON.parse(event.data) as CallSocketMessage;
      console.info('CALL_WS_RECEIVE', getSafeMessageType(message));
      onMessage(message);
    } catch {
      onError('Invalid call signaling message.');
    }
  });
  socket.addEventListener('close', () => {
    if (pingTimer !== null) {
      window.clearInterval(pingTimer);
      pingTimer = null;
    }
    document.removeEventListener('visibilitychange', sendVisibility);
    console.info('CALL_WS_CLOSE', username);
    onClose?.();
    onError('Call signaling disconnected. Reconnecting...');
  });

  return {
    socket,
    ready,
    send(message) {
      console.info('CALL_WS_SEND', getSafeMessageType(message));
      socket.send(JSON.stringify(message));
    },
    close() {
      socket.close();
    },
  };
}

function getSafeMessageType(message: unknown) {
  if (!message || typeof message !== 'object' || !('type' in message)) {
    return 'unknown';
  }

  const type = (message as { type?: unknown }).type;
  return typeof type === 'string' ? type : 'unknown';
}
